@file:Suppress("SpellCheckingInspection", "unused")

package tools

import api.ResourceType
import io.ktor.client.*
import io.ktor.client.request.*
import io.ktor.http.*
import io.ktor.utils.io.core.*
import kotlinx.serialization.Serializable

@Serializable
class FileFormat(val suffix: String, val mime: String, val label: String) {

    fun isPdf() = suffix == "pdf"

    fun isHtml() = suffix == "html"

    fun isZip() = suffix == "zip"

    fun resourceType(): ResourceType {
        return when {
            isZip() -> ResourceType.WWW_APP
            image.any { it.mime == mime } -> ResourceType.IMAGE
            video.any { it.mime == mime } -> ResourceType.VIDEO
            videoAlpha.any { it.mime == mime } -> ResourceType.VIDEO_ALPHA
            extra.any { it.mime == mime } -> ResourceType.WWW_APP
            doc.any { it.mime == mime } -> ResourceType.DOCUMENT
            text.any { it.mime == mime } -> ResourceType.TEXT
            audio.any { it.mime == mime } -> ResourceType.AUDIO
            else -> ResourceType.UNDEFINED
        }
    }

    companion object {
        fun fromMime(mime: String): FileFormat {
            return doc.firstOrNull { it.mime == mime }
                ?: video.firstOrNull { it.mime == mime }
                ?: videoAlpha.firstOrNull { it.mime == mime }
                ?: image.firstOrNull { it.mime == mime }
                ?: extra.firstOrNull { it.mime == mime }
                ?: text.firstOrNull { it.mime == mime }
                ?: audio.firstOrNull { it.mime == mime }
                ?: error("Unsupported file type '$mime'")
        }

        suspend fun fromMimeOnline(url: String): FileFormat {
            return HttpClient().use {
                val result = it.get(url)
                result.takeIf { it.status.isSuccess() }
                    ?.contentType()
                    ?.withoutParameters()
                    ?.let { fromMime("${it.contentType}/${it.contentSubtype}") }
                    ?: error("file-format check fail ${result.status} of '$url'")
            }
        }

        fun fromMimeOrNull(mime: String): FileFormat? = runCatching { fromMime(mime) }
            .onFailure { it.message?.also(::println) }
            .getOrNull()

        fun fromSuffix(suffix: String): FileFormat {
            return doc.firstOrNull { it.suffix == suffix }
                ?: video.firstOrNull { it.suffix == suffix }
                ?: videoAlpha.firstOrNull { it.suffix == suffix }
                ?: image.firstOrNull { it.suffix == suffix }
                ?: extra.firstOrNull { it.suffix == suffix }
                ?: text.firstOrNull { it.suffix == suffix }
                ?: audio.firstOrNull { it.suffix == suffix }
                ?: error("Unsupported file suffix type '$suffix'")
        }

        val extra by lazy {
            listOf(
                FileFormat("zip", "application/zip", "ZIP")
            )
        }

        val doc by lazy {
            listOf(
                FileFormat(
                    "doc",
                    "application/msword",
                    "Microsoft Word"
                ),
                FileFormat(
                    "docx",
                    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                    "Microsoft Word (OpenXML)"
                ),

                FileFormat(
                    "xls",
                    "application/vnd.ms-excel",
                    "Microsoft Excel"
                ),
                FileFormat(
                    "xlsx",
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    "Microsoft Excel (OpenXML)"
                ),

                FileFormat(
                    "ppt",
                    "application/vnd.ms-powerpoint",
                    "Microsoft PowerPoint"
                ),
                FileFormat(
                    "pptx",
                    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
                    "Microsoft PowerPoint (OpenXML)"
                ),

                FileFormat(
                    "odt",
                    "application/vnd.oasis.opendocument.text",
                    "OpenDocument text document"
                ),
                FileFormat(
                    "odp",
                    "application/vnd.oasis.opendocument.presentation",
                    "OpenDocument presentation document"
                ),
                FileFormat(
                    "ods",
                    "application/vnd.oasis.opendocument.spreadsheet",
                    "OpenDocument spreadsheet document"
                ),

                FileFormat(
                    "rtf",
                    "application/rtf",
                    "Rich Text Format (RTF)"
                ),
                FileFormat(
                    "pdf",
                    "application/pdf",
                    "Adobe Portable Document Format (PDF)"
                ),
            )
        }

        val video by lazy {
            listOf(
                FileFormat("mp4", "video/mp4", "MPEG-4 Video"),
                FileFormat("avi", "video/x-msvideo", "Audio Video Interleave (AVI)"),
                FileFormat("webm", "video/webm", "WebM Video"),
                FileFormat("mov", "video/quicktime", "Quicktime video"),
                FileFormat("m4v", "video/x-m4v", "M4v"),
                FileFormat("mkv", "video/x-matroska", "Matroska Video"),
                FileFormat("mpg", "video/mpeg", "MPEG Video"),
            )
        }

        val videoAlpha by lazy {
            listOf(
                FileFormat("mov", "video/quicktime", "Apple ProRes video"),
            )
        }

        val image by lazy {
            listOf(
                FileFormat("jpeg", "image/jpeg", "JPEG"),
                FileFormat("pjpg", "image/pjpeg", "PJPEG"),
                FileFormat("png", "image/png", "PNG"),
                FileFormat("webp", "image/webp", "WebP Image"),
            )
        }

        val text by lazy {
            listOf(
                FileFormat("txt", "text/plane", "TEXT"),
                FileFormat("html", "text/html", "HTML"),
            )
        }
        val audio by lazy {
            listOf(FileFormat("mp3", "audio/mpeg", "MP3"))
        }

        val audioMime by lazy {
            audio.map { it.mime }
        }

        val docMime by lazy {
            doc.map { it.mime } + doc.map { ".${it.suffix}" }
        }

        val videoMime by lazy {
            video.map { it.mime } + video.map { ".${it.suffix}" }
        }

        val videoAlphaMime by lazy {
            videoAlpha.map { it.mime } + videoAlpha.map { ".${it.suffix}" }
        }

        val imageMime by lazy {
            image.map { it.mime } + image.map { ".${it.suffix}" }
        }

        val extraMime by lazy {
            extra.map { it.mime } + extra.map { ".${it.suffix}" }
        }

    }
}
